import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Loader2, ArrowRight, Store, MapPin, CreditCard, AlertTriangle, XCircle } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await api.get('/orders');
        setOrders(res.data.items || []);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const getStatusBadge = (status, statusDisplay) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      payment_failed: 'bg-red-100 text-red-800',
      paid: 'bg-green-100 text-green-800',
      in_preparazione: 'bg-blue-100 text-blue-800',
      spedito: 'bg-purple-100 text-purple-800',
      in_consegna: 'bg-indigo-100 text-indigo-800',
      consegnato: 'bg-green-100 text-green-800',
      problema_spedizione: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    const labels = {
      pending: 'In attesa di pagamento',
      payment_failed: 'Pagamento fallito',
      paid: 'Pagato',
      in_preparazione: 'In preparazione',
      spedito: 'Spedito',
      in_consegna: 'In consegna',
      consegnato: 'Consegnato',
      problema_spedizione: 'Problema spedizione',
      cancelled: 'Annullato'
    };
    
    const label = statusDisplay?.label || labels[status] || status;
    const className = styles[status] || 'bg-gray-100 text-gray-800';
    
    return { label, className };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'payment_failed':
        return <AlertTriangle className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      case 'paid':
      case 'in_preparazione':
        return <Package className="w-4 h-4" />;
      case 'spedito':
      case 'in_consegna':
        return <Truck className="w-4 h-4" />;
      case 'consegnato':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-8">I Miei Ordini</h1>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-border">
            <Package className="w-16 h-16 mx-auto text-text-muted mb-4" />
            <h2 className="text-xl font-bold text-text-primary mb-2">Nessun ordine</h2>
            <p className="text-text-secondary mb-6">Non hai ancora effettuato nessun ordine</p>
            <Button asChild>
              <Link to="/prodotti">
                Inizia lo Shopping
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => {
              const badge = getStatusBadge(order.status, order.status_display);
              return (
                <Link
                  key={order.id}
                  to={`/ordini/${order.id}`}
                  className="block bg-white p-6 rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm text-text-secondary">
                          {order.order_number}
                        </span>
                        <Badge className={`${badge.className} flex items-center gap-1`}>
                          {getStatusIcon(order.status)}
                          {badge.label}
                        </Badge>
                      </div>
                      
                      {/* Attribution - Chi vende e spedisce */}
                      <p className="text-sm text-text-secondary flex items-center gap-1 mb-2">
                        <Store className="w-3 h-3" />
                        {order.attribution || `Venduto e spedito da ${order.producer_name}`}
                      </p>
                      
                      {/* Status description */}
                      {order.status_display?.description && (
                        <p className="text-xs text-text-muted">
                          {order.status_display.description}
                        </p>
                      )}
                      
                      {/* Shipping info */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {order.shipping_city}
                        </span>
                        <span>
                          {new Date(order.created_at).toLocaleDateString('it-IT', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-lg font-bold text-primary">{formatPrice(order.total)}</p>
                      
                      {/* Complete Payment button for pending/failed orders */}
                      {(order.status === 'pending' || order.status === 'payment_failed') && (
                        <Button 
                          size="sm" 
                          className="mt-2 gap-1"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/ordini/${order.id}`;
                          }}
                          data-testid={`complete-payment-${order.id}`}
                        >
                          <CreditCard className="w-3 h-3" />
                          Completa
                        </Button>
                      )}
                      
                      {/* Tracking number if available */}
                      {order.tracking_number && (
                        <div className="mt-2">
                          <p className="text-xs text-text-muted">Tracking:</p>
                          <p className="font-mono text-xs text-primary">
                            {order.tracking_number}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
